import React from 'react';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import { change as formChange, initialize as formInitialize } from 'redux-form';
import styled from 'styled-components';

import InfoWindowPosition from 'components/InfoWindowSettings/InfoWindowPosition';
import InfoWindowContainer from 'components/InfoWindowSettings/InfoWindowContainer';
import InfoWindowBorder from 'components/InfoWindowSettings/InfoWindowBorder';
import InfoWindowArrow from 'components/InfoWindowSettings/InfoWindowArrow';
import InfoWindowShadow from 'components/InfoWindowSettings/InfoWindowShadow';
import InfoWindowBehavior from 'components/InfoWindowSettings/InfoWindowBehavior';

import { BREAKPOINTS, SCAFFOLDING, TYPOGRAPHY, BUTTON, SETTINGS_GROUP } from 'theme';
import SettingsGroup, { SettingsContentHR } from 'components/Shared/SettingsGroup';
import { TextButton, LinkButton } from 'components/Shared/Buttons';
import { HelperBlock, HR } from 'components/Shared/Scaffolding';
import { InputRow, Checkbox, ColorPicker, Select, ButtonSet, LABEL_POSITION } from 'components/Shared/Forms';
import {
    INFO_WINDOW_STYLES,
    mergeFormAndStyle,
    getDefaultValues,
    INFO_WINDOW_POSITION,
} from 'components/InfoWindowSettings/InfoWindowConstants';
import { editMarkerInfoWindow } from 'modules/markers';
import { getActiveMarker } from 'modules/navigation/activeMarker';
import Collapse from 'components/Shared/Collapse';
import RichTextEditorLoader from 'components/Shared/Forms/RichTextEditor/RichTextEditorLoader';

const styleOptions = INFO_WINDOW_STYLES.map((k, i) => ({
    label: k.label,
    value: i,
}));

const StyledSelect = styled(Select)`
    width: 100%;

    @media (min-width: ${`${BREAKPOINTS.xxl}px`}) {
        width: calc(50% - ${SCAFFOLDING.GUTTER_FACTOR(0.25)});
    }
`;

const StyledCollapse = styled(Collapse)`
    margin-left: -${SCAFFOLDING.GUTTER};
    margin-right: -${SCAFFOLDING.GUTTER};
    padding-left: ${SCAFFOLDING.GUTTER};
    padding-right: ${SCAFFOLDING.GUTTER};
`;

const CollapseContent = styled.div`
    > * {
        margin-top: ${SCAFFOLDING.GUTTER};
    }
`;

const StyledHR = styled(HR)`
    margin-top: ${SCAFFOLDING.GUTTER};
    margin-left: -${SCAFFOLDING.GUTTER};
    margin-right: -${SCAFFOLDING.GUTTER};
`;

const Subheading = styled.h1`
    font-size: ${TYPOGRAPHY.FONT_SIZE.H2};
`;

const AdvancedToggle = styled(LinkButton)`
    padding-top: ${BUTTON.PADDING.Y};
    padding-bottom: ${BUTTON.PADDING.Y};
`;

const StyledButtonSet = styled(ButtonSet)`
    margin-top: ${SCAFFOLDING.GUTTER};
`;

const RichTextError = styled(HelperBlock)`
    margin: -${SETTINGS_GROUP.PADDING.Y} -${SETTINGS_GROUP.PADDING.X};
`;

let InfoWindowForm = ({
    persistentValues,
    defaultValues,
    fontColor,
    backgroundColor,
    showArrow,
    showShadow,
    showBorder,
    handleSubmit,
    handleStyleChange,
    onToggleAdvanced,
    onCancel,
}) => {
    return (
        <form onSubmit={handleSubmit}>
            <SettingsGroup
                name="Enable Popup"
                description="Choose if you would like to show a popup window for this marker"
            >
                <Checkbox
                    inline
                    toggleSwitch
                    name="enabled"
                    label="Include a popup and information on the marker"
                    labelPosition={LABEL_POSITION.LEFT}
                />
            </SettingsGroup>
            <StyledCollapse isOpened={!!persistentValues.enabled}>
                <CollapseContent>
                    <SettingsGroup name="Popup Theme" description="Pick how you want your popup to look">
                        <StyledSelect
                            name="style"
                            options={styleOptions}
                            onChange={(e, value) => handleStyleChange(e, value, persistentValues)}
                        />
                    </SettingsGroup>
                    <SettingsGroup
                        name="Content"
                        description="Specify what you want to display inside your popup"
                        spacing={SCAFFOLDING.GUTTER_FACTOR(0.5)}
                    >
                        {RichTextEditorLoader.HasLoaded && (
                            <div>
                                <InputRow>
                                    <ColorPicker name="background" label="Background Color" />
                                    <ColorPicker name="fontColor" label="Default Font Color" />
                                </InputRow>
                                <SettingsContentHR />
                                <RichTextEditorLoader.Component
                                    name="content"
                                    fontColor={fontColor}
                                    backgroundColor={backgroundColor}
                                />
                            </div>
                        )}
                        {!RichTextEditorLoader.HasLoaded && (
                            <RichTextError highlight="danger" inverse>
                                <p>
                                    <strong>There was an error loading the content editor.</strong>
                                </p>
                                <p>
                                    Please make sure your browser is up to date and try again. If the problem persists,
                                    email us at <a href="mailto:support@snazzymaps.com">support@snazzymaps.com</a>.
                                </p>
                            </RichTextError>
                        )}
                    </SettingsGroup>
                    <SettingsGroup
                        name="Position"
                        description="Choose where to display the popup with respect to the marker"
                    >
                        <InfoWindowPosition name="position" />
                    </SettingsGroup>
                </CollapseContent>

                <StyledCollapse isOpened={persistentValues.showAdvanced}>
                    <CollapseContent>
                        <StyledHR />
                        <header>
                            <Subheading>Advanced Settings</Subheading>
                            <p>Customize the look of your marker popup further with the options below.</p>
                        </header>
                        <SettingsGroup
                            name="Container"
                            description="Specify the settings related to the popup's container"
                        >
                            <InfoWindowContainer defaultValues={defaultValues.container} />
                        </SettingsGroup>
                        <SettingsGroup name="Border" description="Specify the settings related to the popup's border">
                            <InfoWindowBorder showBorder={showBorder} defaultValues={defaultValues.border} />
                        </SettingsGroup>
                        <SettingsGroup name="Arrow" description="Specify the settings related to the popup's arrow">
                            <InfoWindowArrow showArrow={showArrow} defaultValues={defaultValues.arrow} />
                        </SettingsGroup>
                        <SettingsGroup name="Shadow" description="Specify the settings related to the popup's shadow">
                            <InfoWindowShadow showShadow={showShadow} defaultValues={defaultValues.shadow} />
                        </SettingsGroup>
                        <SettingsGroup
                            name="Behavior"
                            description="Specify the settings related to the popup's behavior"
                        >
                            <InfoWindowBehavior />
                        </SettingsGroup>
                    </CollapseContent>
                </StyledCollapse>

                <StyledHR />

                <AdvancedToggle type="button" onClick={onToggleAdvanced}>
                    {persistentValues.showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
                </AdvancedToggle>
            </StyledCollapse>
            <StyledButtonSet>
                <TextButton primary type="submit">
                    Apply Changes
                </TextButton>
                <TextButton secondary type="button" onClick={onCancel}>
                    Cancel
                </TextButton>
            </StyledButtonSet>
        </form>
    );
};

InfoWindowForm = reduxForm({
    form: 'info-window',
})(InfoWindowForm);

const selector = formValueSelector('info-window');
const mapStateToProps = (state) => {
    const get = (prop) => selector(state, prop);
    const activeMarker = getActiveMarker(state);
    return {
        persistentValues: {
            enabled: get('enabled'),
            showAdvanced: get('showAdvanced'),
            position: get('position'),
            content: get('content'),
            behavior: get('behavior'),
            container: {
                maxWidth: get('container.maxWidth'),
                maxHeight: get('container.maxHeight'),
                top: get('container.top'),
                left: get('container.left'),
            },
        },
        fontColor: get('fontColor'),
        backgroundColor: get('background'),
        showArrow: get('arrow.enabled'),
        showShadow: get('shadow.enabled'),
        showBorder: get('border.enabled'),
        activeMarker,
        initialValues:
            (activeMarker && activeMarker.infoWindow) ||
            mergeFormAndStyle(
                {
                    enabled: false,
                    showAdvanced: false,
                    position: INFO_WINDOW_POSITION.TOP.value,
                    content: 'Your content goes here...',
                    behavior: {
                        openByDefault: false,
                        closeOnMapClick: true,
                        closeWhenOthersOpen: true,
                    },
                },
                0
            ),
        defaultValues: getDefaultValues(get('style')),
    };
};

const showAdvanced = () => {
    return (dispatch, getState) => {
        const state = getState();
        const currentState = selector(state, 'showAdvanced');
        dispatch(formChange('info-window', 'showAdvanced', !currentState));
    };
};

const mapDispatchToProps = (dispatch) => ({
    onToggleAdvanced: () => {
        dispatch(showAdvanced());
    },
    onSubmit: (values, dispatch, props) => {
        dispatch(editMarkerInfoWindow(props.activeMarker.id, values));
    },
    onSubmitSuccess: (result, dispatch, props) => {
        props.onClose();
    },
    handleStyleChange: (e, value, persistentValues) => {
        dispatch(
            formInitialize('info-window', mergeFormAndStyle(persistentValues, value), {
                keepDirty: false,
            })
        );
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(InfoWindowForm);
